/**
 * Header - Main site header with navigation
 * Split into smaller components in Header/ directory
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, ShoppingBag } from 'lucide-react';
import { useWishlist } from '@/context/WishlistContext';
import { useAuth } from '@/context/AuthContext';
import { useShopify } from '@/context/ShopifyContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import RegionSelector from '@/components/RegionSelector';
import LoginModal from '@/components/LoginModal';
import { getSiteCategories } from '@/lib/api';
import { MobileMenu, DesktopNav, UserMenu, SearchBar, SearchDropdownPanel } from '@/components/Header/index';

// Wrapper to protect RegionSelector from browser extension CSS conflicts
const RegionSelectorWrapper = () => {
  const [isSmScreen, setIsSmScreen] = useState(window.innerWidth >= 640);
  
  useEffect(() => {
    const handleResize = () => setIsSmScreen(window.innerWidth >= 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isSmScreen) return null;
  return <RegionSelector />;
};

// Default categories to show immediately while API loads
const DEFAULT_CATEGORIES = [
  { slug: 'fashion', title: "Women's Fashion", active: true, show_in_header: true, subcategories: [] },
  { slug: 'beauty', title: 'Beauty', active: true, show_in_header: true, subcategories: [] },
  { slug: 'lifestyle', title: 'Lifestyle', active: true, show_in_header: true, subcategories: [] },
];

const Header = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const { wishlistCount } = useWishlist();
  const { isAuthenticated, user } = useAuth();
  const { itemCount, setCartOpen } = useShopify();
  const { isShopifyEnabled } = useSiteSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAccountLoginModal, setShowAccountLoginModal] = useState(false);
  const [loginContext, setLoginContext] = useState('account'); // 'account' or 'favorites'

  // Close search when route changes
  useEffect(() => {
    setSearchOpen(false);
  }, [location.pathname]);

  // Handle logo click - refresh if already on home page
  const handleLogoClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.location.reload();
    }
    // Otherwise, let the Link navigate normally
  };

  const handleAccountClick = () => {
    if (isAuthenticated) {
      navigate('/profile');
    } else {
      setLoginContext('account');
      setShowAccountLoginModal(true);
    }
  };

  const handleWishlistClick = () => {
    if (isAuthenticated) {
      navigate('/favorites');
    } else {
      setLoginContext('favorites');
      setShowAccountLoginModal(true);
    }
  };

  const handleLoginSuccess = () => {
    setShowAccountLoginModal(false);
    navigate(loginContext === 'favorites' ? '/favorites' : '/profile');
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      // Use deduplicated and cached getSiteCategories
      const data = await getSiteCategories();
      if (data && data.length > 0) {
        setCategories(data);
      }
    } catch (error) {
      // Keep using DEFAULT_CATEGORIES on error
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // Build menu structure from dynamic categories - filter by show_in_header
  const menuStructure = [
    ...categories.filter(cat => cat.active !== false && cat.show_in_header !== false).map(cat => ({
      href: `/${cat.slug}`,
      label: cat.title,
      key: cat.slug,
      subpages: (cat.subcategories || []).map(sub => ({
        href: `/${cat.slug}/${sub.id}`,
        label: sub.name
      }))
    })),
    { href: '/search', label: 'Shop All', key: 'shop-all' },
  ];

  // Simple nav links from categories - filter by show_in_header
  const navLinks = [
    ...categories.filter(cat => cat.active !== false && cat.show_in_header !== false).map(cat => ({
      href: `/${cat.slug}`,
      label: cat.title
    })),
    { href: '/search', label: 'Shop All' },
  ];

  return (
    <div className="sticky top-0 z-50">
      <header className="bg-white">
        <div className="px-4 md:px-8 bg-white">
          <div className="flex items-center justify-between h-20 md:h-24 bg-white">
            {/* Mobile Menu */}
            <div className="md:hidden">
              <MobileMenu
                isOpen={mobileMenuOpen}
                onOpenChange={setMobileMenuOpen}
                menuStructure={menuStructure}
                expandedSection={expandedSection}
                onToggleSection={toggleSection}
              />
            </div>

            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center"
              data-testid="logo-link"
              onClick={handleLogoClick}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_4f5c0ea9-425c-4471-ae6d-15b5f34fda60/artifacts/dtqe730w_logo.png"
                alt="The Cool-Girl Cut"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <DesktopNav navLinks={navLinks} />

            {/* Actions */}
            <div className="flex items-center space-x-2 md:space-x-4">
              {/* Search */}
              <SearchBar isOpen={searchOpen} onToggle={setSearchOpen} />

              {/* Wishlist - Navigate directly to favorites page or show login */}
              <button
                onClick={handleWishlistClick}
                className="p-2 hover:bg-site-bg-dark hover:text-white transition-colors relative"
                data-testid="wishlist-btn"
              >
                <Heart className="w-5 h-5" />
                {isAuthenticated && wishlistCount > 0 && (
                  <span 
                    className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs font-mono w-5 h-5 flex items-center justify-center"
                    data-testid="wishlist-count"
                  >
                    {wishlistCount}
                  </span>
                )}
              </button>

              {/* Shopping Cart - Shopify (only shown when enabled) */}
              {isShopifyEnabled && (
                <button
                  onClick={() => setCartOpen(true)}
                  className="p-2 hover:bg-site-bg-dark hover:text-white transition-colors relative"
                  data-testid="cart-btn"
                >
                  <ShoppingBag className="w-5 h-5" />
                  {itemCount > 0 && (
                    <span 
                      className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs font-mono w-5 h-5 flex items-center justify-center rounded-full"
                      data-testid="cart-count"
                    >
                      {itemCount}
                    </span>
                  )}
                </button>
              )}

              {/* User/Profile */}
              <UserMenu 
                isAuthenticated={isAuthenticated} 
                user={user} 
                onClick={handleAccountClick} 
              />

              {/* Region Selector - protected from browser extensions */}
              <RegionSelectorWrapper />
            </div>
          </div>
        </div>

        {/* Search dropdown panel - inside header, before bottom border */}
        <SearchDropdownPanel isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
        
        {/* Bottom border line */}
        <div className="border-b border-site-border-strong" />
      </header>

      {/* Account Login Modal */}
      <LoginModal
        isOpen={showAccountLoginModal}
        onClose={() => setShowAccountLoginModal(false)}
        onSuccess={handleLoginSuccess}
        context="account"
      />
    </div>
  );
};

export default Header;
